/* eslint-disable no-console */
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomLayout from "../../Core/Layout/CustomLayout";
import Button from "../../Core/Components/CustomButton";
import { CustomInputWithController } from "../../Core/Components/CustomInput";
import { PopoverDropdown } from "../../Core/Components/CustomDropdown";
import useHubStationOnAdd from "../../Utils/useHubStationOnAdd";
import { addAsset, editAsset } from "../../Redux/APIs/powerAfricaAPI";
import { updateAsset } from "../../Redux/Slices/powerAfrica.slice";
import { assetTypeOptions } from "./data";

const LabelAndInput = ({
  label,
  control,
  formState,
  name,
  placeholder,
  rules,
  focus,
  setFocus,
}) => {
  return (
    <div className="">
      <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
        {label}
      </p>
      <CustomInputWithController
        intent="createUser"
        control={control}
        formState={formState}
        focus={focus}
        setFocus={(e) => setFocus(e)}
        name={name}
        placeholder={placeholder}
        rules={rules}
      />
    </div>
  );
};

const AddAsset = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const { currentAsset } = useSelector((state) => state.powerAfrica);

  const hubStations = useHubStationOnAdd();

  const { handleSubmit, control, formState, setValue, clearErrors, setError } =
    useForm({
      defaultValues: {
        assetTypeId: id ? currentAsset?.type : null,
        assetTypeName: id ? currentAsset?.assetTypeName : "",
        registrationNo: id ? currentAsset.registrationNo : "",
        modelNo: id ? currentAsset.modelNo : "",
        assetTag: id ? currentAsset.assetTag : "",
        hubId: id ? currentAsset.hubId : "",
        hubName: id ? currentAsset.hubName : "",
      },
    });

  const [focus, setFocus] = useState({
    registrationNo: false,
    modelNo: false,
    assetTag: false,
  });

  const rules = {
    required: "Required.",
  };

  const assetNameError = formState?.errors?.assetTypeName?.message;
  const hubNameError = formState?.errors?.hubName?.message;

  const onCancel = () => navigate(-1);

  const onAdd = async (e) => {
    const payload = e;
    if (id) {
      payload.powerAfricaAssetId = id;

      const editResponse = await dispatch(editAsset(payload));

      if (
        editResponse?.payload?.response &&
        editResponse?.payload?.response?.status !== 200
      ) {
        setError("registrationNo", {
          type: "server",
          shouldFocus: true,
          message: editResponse?.payload?.response?.data?.message,
        });
      } else {
        dispatch(
          updateAsset({
            key: "showToastMessage",
            value: {
              visible: true,
              data: {
                title: "1 Asset Edited",
                description: "1 asset was edited successfully.",
              },
            },
          }),
        );

        navigate(-1);
      }
    } else {
      const addResponse = await dispatch(addAsset(payload));

      if (
        addResponse?.payload?.response &&
        addResponse?.payload?.response?.status !== 200
      ) {
        setError("registrationNo", {
          type: "server",
          shouldFocus: true,
          message: addResponse?.payload?.response?.data?.message,
        });
      } else {
        dispatch(
          updateAsset({
            key: "showToastMessage",
            value: {
              visible: true,
              data: {
                title: "1 Asset Added",
                description: "1 new asset was added successfully.",
              },
            },
          }),
        );

        navigate(-1);
      }
    }
  };

  return (
    <CustomLayout pageName="Assets" nestedName="Agri-Tech" hideFooter>
      <div className="px-6 pt-6 bg-background-white">
        <div className="flex items-center justify-between pb-6">
          <p className="font-poppins text-base font-medium capitalize">
            {id ? "Edit asset" : "Add a new asset"}
          </p>

          <div className="flex items-center gap-x-3">
            <Button
              size="filter"
              width="generateUser"
              colors="grey"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              size="filter"
              width="generateUser"
              type="submit"
              onClick={handleSubmit(onAdd)}
            >
              {id ? "Save" : "Add"} Asset
            </Button>
          </div>
        </div>

        <div className="w-full bg-light-grey h-[1px]" />
      </div>

      <div className="h-full bg-background-white p-6 flex flex-col gap-y-6">
        <div className="">
          <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
            Asset Type
          </p>

          <Controller
            name="assetTypeName"
            rules={rules}
            control={control}
            render={({ field }) => (
              <PopoverDropdown
                {...field}
                enableSearch
                searchPlaceholder="Search Asset"
                placeholder="Select Asset"
                className="max-h-[200px] overflow-y-scroll"
                formState={formState}
                options={assetTypeOptions}
                onSelect={(data) => {
                  setValue("assetTypeName", data.value);
                  setValue("assetTypeId", data.id);
                  clearErrors("assetTypeName");
                }}
              />
            )}
          />

          <p
            className={`font-poppins text-xs font-light capitalize leading-4.5 ${
              assetNameError ? "text-alert-red visible mt-2" : "invisible"
            }`}
          >
            {assetNameError}
          </p>
        </div>

        <LabelAndInput
          label="Registration No."
          control={control}
          formState={formState}
          name="registrationNo"
          placeholder="Enter Number"
          rules={rules}
          focus={focus.registrationNo}
          setFocus={(state) => setFocus({ ...focus, registrationNo: state })}
        />

        <LabelAndInput
          label="Model Number"
          control={control}
          formState={formState}
          name="modelNo"
          placeholder="Enter Number"
          rules={rules}
          focus={focus.modelNo}
          setFocus={(state) => setFocus({ ...focus, modelNo: state })}
        />

        <LabelAndInput
          label="Asset Tag"
          control={control}
          formState={formState}
          name="assetTag"
          placeholder="Enter Number"
          rules={rules}
          focus={focus.assetTag}
          setFocus={(state) => setFocus({ ...focus, assetTag: state })}
        />

        <div className="">
          <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
            Hub Station
          </p>

          <Controller
            name="hubName"
            rules={rules}
            control={control}
            render={({ field }) => (
              <PopoverDropdown
                {...field}
                enableSearch
                searchPlaceholder="Search Hub Station"
                placeholder="Assign Hub Station"
                className="max-h-[200px] overflow-y-scroll"
                formState={formState}
                options={hubStations}
                onSelect={(data) => {
                  setValue("hubName", data.value);
                  setValue("hubId", data.id);
                  clearErrors("hubName");
                }}
              />
            )}
          />

          <p
            className={`font-poppins text-xs font-light capitalize leading-4.5 ${
              hubNameError ? "text-alert-red visible mt-2" : "invisible"
            }`}
          >
            {hubNameError}
          </p>
        </div>
      </div>
    </CustomLayout>
  );
};

export default AddAsset;
